// ==========================================================================
// FILE: /app/components/onboarding/steps/AuthMatrixStep.tsx
// ==========================================================================
//
// DUMMY / ONBOARDING PREVIEW STEP
// --------------------------------------------------------------------------
// This is a static walkthrough version of AuthMatrix.tsx. There are NO API
// calls here (no gxAuthMatrixViewSummaryApi / gxAuthMatrixViewApi / etc.) —
// every piece of data is local fixture state shaped exactly like the real
// endpoints respond, so this step can be swapped for the real component
// later without changing any of the child components it renders
// (TransactionTypesMenu, ContentTitle, AuthMatrixCard, AddThresholdBtn,
// AuthGroupSelector, AppDrawer, GxTextBox, GxIconButton, RequiredMessage,
// NoDataFound).
//
// NOTE ON IMPORT PATHS: AuthMatrix.tsx imports its siblings with relative
// paths ("./TransactionTypesMenu", "./AuthMatrixCard", etc.) because this
// step file lives in a different folder (/app/components/onboarding/steps/),
// the imports below use the "@/app/components/authmatrix/..." alias as a
// placeholder — update these to match wherever your AuthMatrix folder
// actually sits in the project.
// --------------------------------------------------------------------------

import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
// import "@/app/components/authmatrix/AuthMatrix.scss";

import "../../../page/default/admin-menu/AdminMenu.scss"


import GxIconButton from "@/app/components/common/actions/buttons/GxIconButton";
import GxTextBox from "@/app/components/common/actions/inputs-b/GxTextBox";
import AppDrawer from "@/app/models/drawer/AppDrawer";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import type { AuthGroupItem } from "@/app/page/default/admin-menu/auth-matrix/AuthGroupSelector";
import { TransactionTypesMenu } from "@/app/page/default/admin-menu/auth-matrix/TransactionTypesMenu";
import { ContentTitle } from "@/app/page/default/admin-menu/auth-matrix/ContentTitle";
import AuthGroupSelector from "@/app/page/default/admin-menu/auth-matrix/AuthGroupSelector";
import { AuthMatrixCard } from "@/app/page/default/admin-menu/auth-matrix/AuthMatrixCard";
import { AddThresholdBtn } from "@/app/page/default/admin-menu/auth-matrix/AddThresholdBtn";

// --------------- Types (mirrors AuthMatrix.tsx) ---------------

interface ApprovalLevelDto {
  authGroupId: number;
  groupCode?: string;
  approvalLevel: number;
}

interface authMatrixCard {
  id: string;
  from: string;
  to: string;
  approvers: string;
  editDelete: string;
  accountId?: number;
  fromAmount?: number;
  toAmount?: number;
  requiredApprovers?: number;
  paymentTypeId?: number;
  currencyCode2?: string;
  countryCode2?: string;
  approvalMode?: string;
  approvalLevels?: ApprovalLevelDto[];
}

interface DummyTransactionType {
  id: string;
  paymentTypeId: number;
  paymentTypeName: string;
  paymentTypeCode: string;
  subFeatureId: number;
  countryCode2: string;
}

const isApproverDropDownTransactionType = (label?: string) => {
  return label === "Beneficiary Approvals" || label === "Payer Approvals";
};

// --------------- Fixture data ---------------
// Shaped the same way gxAuthMatrixViewSummaryApi's `data` array looks
// (see the sample response used for TransactionTypesMenu).

const DUMMY_TRANSACTION_TYPES: DummyTransactionType[] = [
  {
    id: "121",
    paymentTypeId: 121,
    paymentTypeName: "Beneficiary Approvals",
    paymentTypeCode: "AE-23610",
    subFeatureId: 225,
    countryCode2: "AE",
  },
  {
    id: "122",
    paymentTypeId: 122,
    paymentTypeName: "Own Account Transfer",
    paymentTypeCode: "AE-23611",
    subFeatureId: 226,
    countryCode2: "AE",
  },
  {
    id: "123",
    paymentTypeId: 123,
    paymentTypeName: "Within Bank Transfers",
    paymentTypeCode: "AE-23612",
    subFeatureId: 227,
    countryCode2: "AE",
  },
  {
    id: "124",
    paymentTypeId: 124,
    paymentTypeName: "Domestic Transfers",
    paymentTypeCode: "AE-23613",
    subFeatureId: 228,
    countryCode2: "AE",
  },
  {
    id: "128",
    paymentTypeId: 128,
    paymentTypeName: "International Transfers",
    paymentTypeCode: "AE-23617",
    subFeatureId: 230,
    countryCode2: "AE",
  },
];

// Shaped like gxAuthMatrixViewApi's `data` array (see sample response for
// paymentTypeId 123 / "Within Bank Transfers") — used as the PRODUCT_BASED
// card list for whichever non-approver transaction type is selected.
const DUMMY_PRODUCT_CARDS_BY_PAYMENT_TYPE: Record<number, authMatrixCard[]> = {
  123: [
    {
      id: "571",
      from: "1.00",
      to: "9.00",
      approvers: "2",
      editDelete: "",
      fromAmount: 1,
      toAmount: 9,
      requiredApprovers: 2,
      paymentTypeId: 123,
      currencyCode2: "AED",
      countryCode2: "AE",
      approvalMode: "SEQUENTIAL",
      approvalLevels: [
        { authGroupId: 13, groupCode: "A", approvalLevel: 1 },
        { authGroupId: 14, groupCode: "B", approvalLevel: 2 },
      ],
    },
    {
      id: "573",
      from: "9.01",
      to: "99.00",
      approvers: "2",
      editDelete: "",
      fromAmount: 9.01,
      toAmount: 99,
      requiredApprovers: 2,
      paymentTypeId: 123,
      currencyCode2: "AED",
      countryCode2: "AE",
      approvalMode: "SEQUENTIAL",
      approvalLevels: [
        { authGroupId: 13, groupCode: "A", approvalLevel: 1 },
        { authGroupId: 14, groupCode: "B", approvalLevel: 2 },
      ],
    },
  ],
};

// Shaped like useAuthGroups()'s resolved list — every group available to
// assign to a level.
const DUMMY_AUTH_GROUPS_LIST: { authGroupId: number; groupCode: string; groupName: string }[] = [
  { authGroupId: 13, groupCode: "A", groupName: "CEO" },
  { authGroupId: 14, groupCode: "B", groupName: "Staff" },
  { authGroupId: 15, groupCode: "C", groupName: "Finance Manager" },
];

// Seeded Beneficiary/Payer approver rows — mirrors approvalLevels from the
// sample view response.
const DUMMY_APPROVER_AUTH_GROUPS: AuthGroupItem[] = [
  { level: 1, groupId: 13 },
  { level: 2, groupId: 14 },
];

const DUMMY_MAX_AMOUNT = "100000";

// --------------- Component ---------------

const AuthMatrixStep: FC = () => {
  const { t } = useTranslation();

  const [selectedTransactionType, setSelectedTransactionType] =
    useState<DummyTransactionType | null>(DUMMY_TRANSACTION_TYPES[0]);

  const isApproverDropDownType = isApproverDropDownTransactionType(
    selectedTransactionType?.paymentTypeName,
  );

  // PRODUCT_BASED cards, keyed by the selected transaction type.
  const [productCardsByType, setProductCardsByType] = useState<
    Record<number, authMatrixCard[]>
  >(DUMMY_PRODUCT_CARDS_BY_PAYMENT_TYPE);

  const productCards: authMatrixCard[] = selectedTransactionType
    ? productCardsByType[selectedTransactionType.paymentTypeId] || []
    : [];

  // Beneficiary/Payer approver drawer state.
  const [isApproverDrawerOpen, setIsApproverDrawerOpen] = useState(false);
  const [approverAuthGroups, setApproverAuthGroups] = useState<AuthGroupItem[]>(
    DUMMY_APPROVER_AUTH_GROUPS,
  );
  const [approverApprovalMode] = useState<string>("SEQUENTIAL");

  const approverGroupsCount =
    approverAuthGroups.filter((g) => g.groupId != null).length ||
    approverAuthGroups.length;

  const canAddApproverAuthGroup =
    DUMMY_AUTH_GROUPS_LIST.length > approverAuthGroups.length;

  // --------------- Handlers (all local — no network calls) ---------------

  const handleSelectedTransactionTypes = (item: DummyTransactionType) => {
    setSelectedTransactionType(item);
    setIsApproverDrawerOpen(false);
  };

  const handleAddApproverAuthGroup = () => {
    setApproverAuthGroups((prev) => {
      const nextLevel =
        prev.length > 0 ? Math.max(...prev.map((g) => g.level)) + 1 : 1;
      return [...prev, { level: nextLevel, groupId: null }];
    });
  };

  const handleDeleteApproverAuthGroup = (level: number) => {
    setApproverAuthGroups((prev) => prev.filter((g) => g.level !== level));
  };

  const handleApproverGroupChange = (
    level: number,
    value: { authGroupId: number; groupCode: string } | null,
  ) => {
    setApproverAuthGroups((prev) =>
      prev.map((group) =>
        group.level === level
          ? { ...group, groupId: value?.authGroupId ?? null }
          : group,
      ),
    );
  };

  const getOptionsForApproverRow = (level: number) => {
    const usedElsewhere = new Set(
      approverAuthGroups
        .filter((g) => g.level !== level && g.groupId != null)
        .map((g) => g.groupId),
    );
    return DUMMY_AUTH_GROUPS_LIST.filter(
      (g) => !usedElsewhere.has(g.authGroupId),
    );
  };

  // Dummy "add threshold" — appends a placeholder card after the last one.
  const handleAddThreshold = () => {
    if (!selectedTransactionType) return;
    const paymentTypeId = selectedTransactionType.paymentTypeId;
    setProductCardsByType((prev) => {
      const existing = prev[paymentTypeId] || [];
      const lastTo = existing.length
        ? Number.parseFloat(existing[existing.length - 1].to)
        : 0;
      const newFrom = (lastTo + 0.01).toFixed(2);
      const newCard: authMatrixCard = {
        id: `dummy-${Date.now()}`,
        from: newFrom,
        to: DUMMY_MAX_AMOUNT,
        approvers: "1",
        editDelete: "",
        fromAmount: Number.parseFloat(newFrom),
        toAmount: Number.parseFloat(DUMMY_MAX_AMOUNT),
        requiredApprovers: 1,
        paymentTypeId,
        currencyCode2: "AED",
        countryCode2: selectedTransactionType.countryCode2,
        approvalMode: "SEQUENTIAL",
        approvalLevels: [],
      };
      return { ...prev, [paymentTypeId]: [...existing, newCard] };
    });
  };

  // Dummy edit/delete — delete removes the card locally; edit is a no-op
  // placeholder (the real flow opens AddEditAmountCard via the parent Page).
  const handleEditDeleteCard = (item: authMatrixCard) => {
    if (!selectedTransactionType) return;
    const paymentTypeId = selectedTransactionType.paymentTypeId;

    if (item.editDelete === "delete") {
      setProductCardsByType((prev) => ({
        ...prev,
        [paymentTypeId]: (prev[paymentTypeId] || []).filter(
          (c) => c.id !== item.id,
        ),
      }));
      return;
    }

    // "edit" — wire this up to AddEditAmountCard in the real flow.
    console.log("Edit clicked (dummy step):", item);
  };

  return (
    <div className="gx-auth-matrix-flex-wrapper gx-auth-matrix-main-container">
      {/* LEFT MENU */}
      <div className="gx-auth-matrix-left-header-box gx-auth-matrix-left-header-container h-[45vh]! " >
        <div className="gx-auth-matrix-heading">
          <h2 className="gx-p18-sb">{t("auth_matrix_title")}</h2>
          <p className="gx-p12-m">{t("auth_matrix_subtitle")}</p>
        </div>

        <div className="gx-auth-matrix-left-menu-container">
          <TransactionTypesMenu
            data={DUMMY_TRANSACTION_TYPES}
            onSelect={handleSelectedTransactionTypes}
            isLoadingTransactionTypes={false}
            selectedPaymentTypeId={selectedTransactionType?.paymentTypeId}
          />
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="gx-auth-matrix-right-container gx-auth-matrix-right-box h-[45vh]!">
        <ContentTitle
          id={selectedTransactionType?.paymentTypeId?.toString()}
          label={selectedTransactionType?.paymentTypeName}
        />

        <div className="gx-flex-column">
          {isApproverDropDownType ? (
            <div className="gx-approver-section">
              <div>
                <GxTextBox
                  label={t("required_approvers")}
                  value={`${approverGroupsCount} Approver${
                    approverGroupsCount === 1 ? "" : "s"
                  }`}
                  onChange={() => {}} // no-op when clickable
                  clickable={true}
                  onClick={() => setIsApproverDrawerOpen(true)}
                  showStarLabel={false}
                />
              </div>

              <AppDrawer
                title={t("required")}
                subtitle={t("approvers")}
                cancel={t("cancel")}
                submit={t("save")}
                openDrawer={isApproverDrawerOpen}
                onClose={() => setIsApproverDrawerOpen(false)}
                onSubmit={() => setIsApproverDrawerOpen(false)}
                variant="grey"
              >
                <div className="gx-add-edit-amount-card">
                  <div className="gx-auth-grp-header">
                    <p className="gx-p18-b gx-auth-matrix-title">
                      {t("auth_matrix")}
                    </p>
                    <GxIconButton
                      type="add"
                      disabled={!canAddApproverAuthGroup}
                      size="small"
                      onClick={handleAddApproverAuthGroup}
                    />
                  </div>

                  <p className="gx-auth-grp-labels gx-p12-sb">
                    <span>Levels</span>
                    <span>Auth Group</span>
                  </p>

                  <div className="gx-auth-grp-list">
                    {approverAuthGroups.map((group) => (
                      <AuthGroupSelector
                        key={group.level}
                        item={group}
                        dropdownData={getOptionsForApproverRow(group.level)}
                        tooltip="Select an authorization group"
                        canDelete={approverAuthGroups.length > 1}
                        onDelete={() =>
                          handleDeleteApproverAuthGroup(group.level)
                        }
                        onGroupChange={(value) =>
                          handleApproverGroupChange(group.level, value)
                        }
                      />
                    ))}
                  </div>
                </div>
              </AppDrawer>
            </div>
          ) : (
            // PRODUCT_BASED: no accounts, no Account Header — show AuthMatrixCard directly.
            <div className="gx-transition-panel">
              <h1 className="gx-p18-b gx-auth-matrix-title">
                {t("authorization_matrix")}
              </h1>

              <div className="gx-account-list-scroll">
                {productCards.length === 0 ? (
                  <NoDataFound
                    message="No Thresholds Available for This Transaction Type"
                    submessage="There are currently no thresholds configured for the selected transaction type. Please choose a different option or contact support for assistance."
                  />
                ) : (
                  <div className="gx-amount-transaction-card-container">
                    <AuthMatrixCard
                      data={productCards}
                      setEditDeleteCard={handleEditDeleteCard}
                      allowEditAll={true}
                    />
                  </div>
                )}
              </div>

              <div className="gx-add-threshold-btn">
                <AddThresholdBtn
                  disable={
                    productCards.length > 0 &&
                    parseInt(DUMMY_MAX_AMOUNT) ===
                      parseInt(productCards[productCards.length - 1]?.to)
                  }
                  onClick={handleAddThreshold}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthMatrixStep;